import { EHeadingStyleVariant, Heading } from '@outdoorsyco/bonfire';
import classNames from 'classnames';
import querystring from 'query-string';
import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import MaxWidth from '@/components/common/layout/MaxWidth';
import Anchor from '@/components/switchback/Anchor/Anchor';
import SearchHeader, { IHeaderMenu } from '@/components/ui/SearchHeader/SearchHeader';
import { useRouter } from '@/hooks/useRouter';
import { trackGuidedSearchSelected } from '@/services/analytics/search';
import { ESerpGuidedSearchVariationKey } from '@/services/experiments';
import { trackEvent } from '@/services/track-event';
import { IBreadcrumb } from '@/utility/commonDataTypes';
import headerDetails from '@/utility/locality';

import css from './Hero.module.css';

interface IProps {
  title?: string | ReactNode;
  description?: string;
  regionCode?: string;
  urlPath: string;
  breadcrumbs: IBreadcrumb[];
  // preselect a rental category by default and don't render top menu so we can't change it
  lockedSearchCategory?: IHeaderMenu;
  serpGuidedSearchEnabled?: boolean;
  location?: string;
  bounds?: Record<string, string>;
  serpGuidedSearchVariationKey?: string;
  hideBreadcrumbs?: boolean;
  useBFHeadingStyle?: boolean;
  withoutMaxTextWidth?: boolean;
  localitySearchFilters?: Record<string, string>;
}

const Hero: React.FC<IProps> = ({
  title,
  description,
  urlPath,
  regionCode,
  breadcrumbs,
  lockedSearchCategory,
  serpGuidedSearchEnabled,
  location,
  bounds,
  serpGuidedSearchVariationKey,
  hideBreadcrumbs,
  useBFHeadingStyle,
  withoutMaxTextWidth,
  localitySearchFilters,
}) => {
  const header = headerDetails(urlPath, regionCode);
  const router = useRouter();
  const intl = useIntl();

  const quizOnClick = () => {
    const query = querystring.stringify({
      location,
      // If we have bounds, attach them to the location so guided
      // search would know the bounds relates to the location.
      ...(bounds?.['bounds[ne]'] && bounds?.['bounds[sw]']
        ? {
            'location[bounds][ne]': bounds?.['bounds[ne]'],
            'location[bounds][sw]': bounds?.['bounds[sw]'],
          }
        : {}),
      skip_defaults: 'true',
    });
    trackGuidedSearchSelected({ source: 'serp_search' });
    router.push('guided-search?' + query, undefined, {
      shallow: true,
    });
  };

  const subHeaderVariables = {
    GuidedSearchLink: (text: React.ReactNode) => (
      <Anchor
        className={`font-bold underline cursor-pointer ${header?.textColor}`}
        onClick={quizOnClick}>
        {text}
      </Anchor>
    ),
  };

  const defaultHeader = intl.formatMessage(
    {
      defaultMessage:
        'Not sure where to start? Use our <GuidedSearchLink>RV Wizard</GuidedSearchLink> to find the perfect rig.',
      id: 'dKFUQT',
    },
    subHeaderVariables,
  );
  // This object will variation key and the text
  const subHeaders: Record<ESerpGuidedSearchVariationKey, ReactNode> = {
    [ESerpGuidedSearchVariationKey.RV_WIZARD_WHERE_TO_START]: defaultHeader,
    [ESerpGuidedSearchVariationKey.RV_WIZARD_WHICH_RV]: intl.formatMessage(
      {
        defaultMessage:
          'Not sure which RV to pick? Find your fit with <GuidedSearchLink>RV Wizard</GuidedSearchLink>.',
        id: 'IJ8Dex',
      },
      subHeaderVariables,
    ),
    [ESerpGuidedSearchVariationKey.MY_RV_FIT_WHERE_TO_START]: intl.formatMessage(
      {
        defaultMessage:
          'Not sure where to start? Use <GuidedSearchLink>My RV Fit</GuidedSearchLink> to find the perfect rig.',
        id: 'Ewqmtl',
      },
      subHeaderVariables,
    ),
    [ESerpGuidedSearchVariationKey.RV_MATCHMAKER_WHICH_RV]: intl.formatMessage(
      {
        defaultMessage:
          'Not sure which RV to pick? Find your fit with <GuidedSearchLink>RV Matchmaker</GuidedSearchLink>.',
        id: 'VX/f+4',
      },
      subHeaderVariables,
    ),
    [ESerpGuidedSearchVariationKey.RV_NAVIGATOR_GUESSWORK]: intl.formatMessage(
      {
        defaultMessage:
          'Not sure where to start? <GuidedSearchLink>RV Navigator</GuidedSearchLink> takes the guesswork out of finding your perfect rig.',
        id: 'eXpfkl',
      },
      subHeaderVariables,
    ),
    [ESerpGuidedSearchVariationKey.RV_NAVIGATOR_GET_STARTED]: intl.formatMessage(
      {
        defaultMessage:
          'Get started with <GuidedSearchLink>RV Navigator</GuidedSearchLink>: your guide to finding the perfect RV fit.',
        id: 'XhwbdQ',
      },
      subHeaderVariables,
    ),
    [ESerpGuidedSearchVariationKey.RV_SCOUT_GET_STARTED]: intl.formatMessage(
      {
        defaultMessage:
          'Get started with <GuidedSearchLink>RV Scout</GuidedSearchLink>: your guide to finding the perfect RV fit.',
        id: '9qLQLT',
      },
      subHeaderVariables,
    ),
  };

  const trackClickEvent = () => {
    trackEvent({
      event: 'Locality : Performed search',
    });
  };

  const subHeader =
    subHeaders?.[serpGuidedSearchVariationKey as ESerpGuidedSearchVariationKey] || defaultHeader;

  return (
    <div
      className={classNames('flex flex-col w-full', css.hero, {
        // @ts-expect-error fixable: unchecked index access
        [css.heroBackgroundYellow]: header?.bgColor === 'bg-yellowstone',
        // @ts-expect-error fixable: unchecked index access
        [css.heroBackgroundBlue]: header?.bgColor === 'bg-blue-800',
        // @ts-expect-error fixable: unchecked index access
        [css.heroBackgroundGreen]: header?.bgColor === 'bg-green-800',
      })}
      id="serp-hero-section">
      <MaxWidth
        className={classNames('relative justify-between h-full', {
          'h-[320px] align-middle': serpGuidedSearchEnabled,
        })}>
        <div
          className={`hidden lg:block absolute w-full h-full bg-right bg-no-repeat -mx-8 Hero-module_heroImage_3epY9 ${css.heroImage}`}
          style={{ backgroundImage: `url('${header?.imageUrl.src}')` }}
        />
        {!hideBreadcrumbs && (
          <div className="z-10 mt-6 mb-8">
            <Breadcrumbs breadcrumbs={breadcrumbs} textColor={header?.textColor} />
          </div>
        )}
        <div
          className={`z-10 flex flex-col w-full ${useBFHeadingStyle ? 'my-14' : 'my-4'} ${
            serpGuidedSearchEnabled ? 'max-w-[556px]' : ''
          }`}>
          <div
            className={`xxl:px-0 md:pt-0 mb-4 ${header?.textColor} ${withoutMaxTextWidth ? '' : css.maxTextWidth}`}>
            {serpGuidedSearchEnabled || useBFHeadingStyle ? (
              <>
                <Heading
                  variant={EHeadingStyleVariant.H1}
                  className="text-2xl font-medium text-left md:text-[32px] md:!leading-10">
                  {title}
                </Heading>
              </>
            ) : (
              <>
                <h1
                  className="mb-2 text-2xl font-bold leading-tight text-left md:text-3xl md:mb-4"
                  data-test-title>
                  {title}
                </h1>
                {description && <p className="mb-6 text-lg text-left md:text-xl">{description}</p>}
              </>
            )}
          </div>
          <SearchHeader
            isSERP={serpGuidedSearchEnabled}
            isUniversalSearch={!serpGuidedSearchEnabled}
            isFullUniversalSearch
            lockedCategory={lockedSearchCategory}
            localitySearchFilters={localitySearchFilters}
            onSubmit={trackClickEvent}
          />
          {serpGuidedSearchEnabled && (
            <div>
              <p className={`mt-2 ${header?.textColor}`}>{subHeader}</p>
            </div>
          )}
        </div>
      </MaxWidth>
    </div>
  );
};

export default Hero;
